import { VCPM } from '../models/project.model';
import { normalizeProjectClusterOutputInPlace } from './cluster-output-normalization.transform';

/**
 * Ported as-is from ProjectMiddlewareService.updateSavedProjectWithAutoModeinMgmtandWldDomains
 * (project-middleware.service.ts:8-27).
 *
 * Gap E mutation semantics: `project.data = projectData.data` and
 * `project.metadata = projectData.metadata` alias the clone's data/metadata back onto the
 * ORIGINAL input's objects, discarding the deep clone for those two branches. Every mutation
 * below (`wl.workloadDetails['autoMode']`, `clusterConfiguration['autoMode']`) therefore mutates
 * projectData.data in place too, not just the returned object. This is preserved exactly - not a
 * fix. See "Behavior anomalies observed" in the S5 task report.
 */
export function applyAutoModeDefaults(projectData: VCPM.Project): VCPM.Project {
  const project: VCPM.Project = JSON.parse(JSON.stringify(projectData));
  project.data = projectData.data;
  project.metadata = projectData.metadata;
  applyAutoModeDefaultsInPlace(projectData);
  return project;
}

/** Mutation body shared with applyProjectMigrations() - see that function's doc comment. */
function applyAutoModeDefaultsInPlace(projectData: VCPM.Project): void {
  const workloadDomains: VCPM.WorkloadDomain[] | undefined = projectData.data.workloadDomains;
  const managementDomain: VCPM.ManagementDomain | undefined = projectData.data.managementDomain;
  workloadDomains?.forEach((wld) => {
    wld.workloadList?.forEach((wl) => {
      if (
        wl.storageConfiguration &&
        wl.storageConfiguration.storageType === 'VSAN' &&
        wl.storageConfiguration.vsanArchitecture === 'ESA' &&
        !('autoMode' in wl.workloadDetails)
      ) {
        wl.workloadDetails['autoMode'] = false;
      }
    });
  });
  if (
    managementDomain?.configuration &&
    managementDomain?.configuration.storageConfiguration &&
    managementDomain?.configuration.storageConfiguration.storageType === 'VSAN' &&
    managementDomain?.configuration.storageConfiguration.vsanArchitecture === 'ESA' &&
    !('autoMode' in managementDomain?.configuration?.clusterConfiguration)
  ) {
    managementDomain.configuration.clusterConfiguration['autoMode'] = false;
  }
  projectData.data.workloadDomains = workloadDomains;
  projectData.data.managementDomain = managementDomain;
}

/**
 * Ported as-is from
 * ProjectMiddlewareService.updateSavedProjectWithAvgMemoryAndCPUConsumptionInRUPOfWorklaodsInWldDomains
 * (project-middleware.service.ts:29-44).
 *
 * Gap E mutation semantics: same aliasing as applyAutoModeDefaults - project.data/.metadata are
 * references to the original projectData's, so the `avgConsumption`/`avgMemoryConsumption`
 * mutations below apply to projectData.data in place too. Preserved exactly.
 */
export function applyAvgConsumptionDefaults(projectData: VCPM.Project): VCPM.Project {
  const project: VCPM.Project = JSON.parse(JSON.stringify(projectData));
  project.data = projectData.data;
  project.metadata = projectData.metadata;
  applyAvgConsumptionDefaultsInPlace(projectData);
  return project;
}

/** Mutation body shared with applyProjectMigrations() - see that function's doc comment. */
function applyAvgConsumptionDefaultsInPlace(projectData: VCPM.Project): void {
  const workloadDomains: VCPM.WorkloadDomain[] | undefined = projectData.data.workloadDomains;
  workloadDomains?.forEach((wld) => {
    wld.workloadList?.forEach((wl) => {
      if ('vmPercentage' in wl.advancedSettings.resourceUtilizationPlan) {
        wl.advancedSettings.resourceUtilizationPlan['avgConsumption'] = 100;
        wl.advancedSettings.resourceUtilizationPlan['avgMemoryConsumption'] = 100;
      }
    });
  });
  projectData.data.workloadDomains = workloadDomains;
}

/**
 * Migrates hardware configuration from old flat structure to new nested structure.
 * Transforms: flat fields -> cpuBasedConfig + readyNodeBasedConfig + commonConfig.
 *
 * Ported as-is from ProjectMiddlewareService.migrateHardwareConfigurationStructure
 * (project-middleware.service.ts:50-70).
 *
 * Gap E mutation semantics: unlike the two functions above, this one does NOT alias
 * project.data/.metadata back to the original - it is a true deep copy, and projectData itself
 * is never mutated.
 */
export function migrateHardwareConfigurationStructure(projectData: VCPM.Project): VCPM.Project {
  const project: VCPM.Project = JSON.parse(JSON.stringify(projectData));
  migrateHardwareConfigurationStructureInPlace(project);
  return project;
}

/** Mutation body shared with applyProjectMigrations() - see that function's doc comment. */
function migrateHardwareConfigurationStructureInPlace(project: VCPM.Project): void {
  // Migrate Management Domain
  if (project.data?.managementDomain?.configuration?.hardwareConfiguration) {
    project.data.managementDomain.configuration.hardwareConfiguration = transformHardwareConfig(
      project.data.managementDomain.configuration.hardwareConfiguration,
    );
  }

  // Migrate Workload Domains
  project.data?.workloadDomains?.forEach((domain) => {
    domain.workloadList?.forEach((workload) => {
      if (workload.hardwareConfiguration) {
        workload.hardwareConfiguration = transformHardwareConfig(workload.hardwareConfiguration);
      }
    });
  });
}

/**
 * Backfills schema fields introduced after older saved projects were created:
 * vcfDeploymentInputs, storageConfiguration.dataEncryption, hardwareConfigMode inference (for a
 * hardwareConfiguration already in the new nested shape but missing this field), and
 * dataReductionRatio derived from legacy dedup/compression. Applies to both workload-domain
 * workloads and the management domain, which shares the same storageConfiguration/
 * hardwareConfiguration shape.
 *
 * Ported as-is from ProjectMiddlewareService.migrateToNewWorkloadModalStructure
 * (project-middleware.service.ts:25-59), including its two private helpers
 * (backfillDataEncryption/backfillHardwareConfigMode).
 *
 * Gap E mutation semantics: like migrateHardwareConfigurationStructure, this is a true deep copy -
 * projectData itself is never mutated.
 */
export function migrateToNewWorkloadModalStructure(projectData: VCPM.Project): VCPM.Project {
  const project: VCPM.Project = JSON.parse(JSON.stringify(projectData));
  migrateToNewWorkloadModalStructureInPlace(project);
  return project;
}

/** Mutation body shared with applyProjectMigrations() - see that function's doc comment. */
function migrateToNewWorkloadModalStructureInPlace(project: VCPM.Project): void {
  // Management domain config shares the same storageConfiguration/hardwareConfiguration shape as
  // a workload's, so the same encryption/mode backfill applies here too.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mgmtConfig: any = project.data?.managementDomain?.configuration;
  if (mgmtConfig) {
    backfillDataEncryption(mgmtConfig.storageConfiguration);
    backfillHardwareConfigMode(mgmtConfig.hardwareConfiguration);
  }

  project.data?.workloadDomains?.forEach((domain) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    domain.workloadList?.forEach((workload: any) => {
      // 1. Add vcfDeploymentInputs if missing
      if (!workload.vcfDeploymentInputs) {
        workload.vcfDeploymentInputs = { deploymentType: 'HCI' };
      }

      // 2. Add dataEncryption to storageConfiguration if missing
      backfillDataEncryption(workload.storageConfiguration);

      // 3. Infer hardwareConfigMode for saved projects where it is null/undefined
      backfillHardwareConfigMode(workload.hardwareConfiguration);

      // 4. Add dataReductionRatio if missing - derive from existing dedup/compression
      if (!('dataReductionRatio' in workload.workloadDetails)) {
        const dedup = workload.workloadDetails.dedup;
        const compression = workload.workloadDetails.compression;
        workload.workloadDetails.dataReductionRatio = dedup ?? compression ?? null;
      }
    });
  });
}

/** Ported as-is from ProjectMiddlewareService.backfillDataEncryption. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function backfillDataEncryption(storageConfiguration: any): void {
  if (storageConfiguration && !('dataEncryption' in storageConfiguration)) {
    storageConfiguration.dataEncryption = false;
  }
}

/** Ported as-is from ProjectMiddlewareService.backfillHardwareConfigMode. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function backfillHardwareConfigMode(hw: any): void {
  if (hw && !hw.hardwareConfigMode) {
    if (hw.readyNodeBasedConfig?.readyNodeSelection) {
      hw.hardwareConfigMode = 'readynode-based';
    } else if (hw.cpuBasedConfig?.cpuVendor || hw.commonConfig?.cores) {
      hw.hardwareConfigMode = 'cpu-based';
    } else {
      hw.hardwareConfigMode = 'no-preference';
    }
  }
}

/**
 * vSAN Max (dHCI): one-time, additive, output-preserving migration that backfills a reusable
 * `HCICluster` entity + `computeCluster` reference for every existing workload that doesn't have
 * one yet. Never mutates `hardwareConfiguration`/`clusterConfiguration` values - copies them as-is
 * into the new entity. Output normalization aligns legacy names and usage metadata while
 * preserving sizing results and selections.
 *
 * Only handles the HCI side: dHCI didn't exist before this feature, so no pre-existing project
 * can have Compute/Storage data to migrate. A workload already on 'dHCI' (only possible for a
 * project saved after this feature shipped, in which case it already has its own reference) is
 * skipped, as is any workload that already has a reference from a prior run of this migration.
 */
export function migrateReusableClusterEntities(projectData: VCPM.Project): VCPM.Project {
  const project: VCPM.Project = JSON.parse(JSON.stringify(projectData));
  migrateReusableClusterEntitiesInPlace(project);
  normalizeProjectClusterOutputInPlace(project);
  return project;
}

/** Mutation body shared with applyProjectMigrations() - see that function's doc comment. */
function migrateReusableClusterEntitiesInPlace(project: VCPM.Project): void {
  project.data.workloadDomains?.forEach((domain) => {
    domain.workloadList?.forEach((workload) => {
      if (workload.computeCluster || workload.vcfDeploymentInputs?.deploymentType === 'dHCI') {
        return;
      }
      const nextNumber = project.data.nextHciClusterNumber ?? 1;
      const cluster: VCPM.HCICluster = {
        type: 'HCI',
        id: crypto.randomUUID(),
        name: `HCICluster-${nextNumber}`,
        vsanArchitecture: workload.storageConfiguration?.vsanArchitecture ?? '',
        hardwareConfiguration: workload.hardwareConfiguration,
        clusterConfiguration: workload.clusterConfiguration,
      };
      domain.clusterConfigurations = domain.clusterConfigurations ?? [];
      domain.clusterConfigurations.push(cluster);
      workload.computeCluster = { id: cluster.id, name: cluster.name };
      project.data.nextHciClusterNumber = nextNumber + 1;
    });
  });
}

/**
 * Combined entry point for every code path that stores a project from a raw backend response
 * (FetchProjectById, UpdateProject, SaveProject, UpdateProjectMetadata) - previously only
 * FetchProjectById ran this backfill chain, so a save/update response could silently persist an
 * unmigrated shape into `currentProject` until the next full reload.
 *
 * Applies all 5 migrations to a single clone instead of chaining the 5 individually-exported
 * functions above (each of which clones on its own) - same net effect on the returned project's
 * content, since every migration only ever mutates its own clone in place and returns it, but
 * avoids paying for 5 sequential full JSON.stringify/JSON.parse passes on every call. Order
 * matches the existing FetchProjectById chain exactly.
 */
export function applyProjectMigrations(projectData: VCPM.Project): VCPM.Project {
  const project: VCPM.Project = JSON.parse(JSON.stringify(projectData));
  applyAutoModeDefaultsInPlace(project);
  applyAvgConsumptionDefaultsInPlace(project);
  migrateHardwareConfigurationStructureInPlace(project);
  migrateToNewWorkloadModalStructureInPlace(project);
  migrateReusableClusterEntitiesInPlace(project);
  normalizeProjectClusterOutputInPlace(project);
  return project;
}

/**
 * Narrows a full HCI `ClusterConfiguration` down to the dHCI-Compute shape. Needed whenever an
 * `HCICluster` entity is picked/mirrored as a dHCI workload's Compute side (or its owning
 * `HCICluster` is later updated and fan-out-synced to that workload) - the two shapes overlap on
 * exactly these five fields, and an `HCICluster`'s Storage-only fields
 * (operationsReserve/hostRebuildReserve/storageEfficiencyPreference) simply don't carry over.
 */
export function toComputeClusterConfiguration(clusterConfiguration: VCPM.ClusterConfiguration): VCPM.ComputeClusterConfiguration {
  return {
    clusterType: clusterConfiguration.clusterType,
    accountForSwapSpace: clusterConfiguration.accountForSwapSpace,
    growthFactor: clusterConfiguration.growthFactor,
    cpuHeadroomSteadyState: clusterConfiguration.cpuHeadroomSteadyState,
    cpuHeadroomHostFailureScenario: clusterConfiguration.cpuHeadroomHostFailureScenario,
  };
}

/**
 * Transforms a single hardware configuration from old flat structure to new nested structure.
 * Ported as-is from ProjectMiddlewareService.transformHardwareConfig (project-middleware.service.ts:75-142).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformHardwareConfig(hw: any): VCPM.HardwareConfiguration {
  // If already in new structure, return as-is. Requiring BOTH commonConfig AND cpuBasedConfig was
  // too strict: a readynode-based (or no-preference) hardwareConfiguration legitimately has
  // commonConfig + readyNodeBasedConfig but no cpuBasedConfig at all (cpuBasedConfig only matters
  // for cpu-based mode) - such an object incorrectly failed this guard and fell through to the old-
  // flat-structure migration below, which looks for fields (hw.readyNodeSelection, hw.sockets, ...)
  // that don't exist on an already-nested object, silently dropping readyNodeBasedConfig entirely
  // and zeroing every commonConfig field via the `?? 0` defaults. commonConfig plus EITHER
  // mode-specific sub-group is sufficient evidence this object is already fully nested. A nested
  // no-preference/use-existing-cluster config has neither sub-group at all (no hardware selection
  // to store) - its hardwareConfigMode value is the only other evidence available, so accept that too.
  const isAlreadyNested =
    hw.commonConfig &&
    (hw.cpuBasedConfig || hw.readyNodeBasedConfig || hw.hardwareConfigMode === 'no-preference' || hw.hardwareConfigMode === 'use-existing-cluster');
  if (isAlreadyNested) {
    return hw;
  }

  // Create new structure
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transformed: any = {
    hardwareConfigMode: hw.hardwareConfigMode || 'cpu-based',
  };

  // === Migrate CPU-based specific fields ===
  if (hw.cpuVendor || hw.cpuModel) {
    transformed.cpuBasedConfig = {
      cpuVendor: hw.cpuVendor || '',
      cpuGeneration: hw.cpuGeneration || '',
      cpuSeries: hw.cpuSeries || '',
      cpuCategory: hw.cpuCategory || '',
      cpuModel: hw.cpuModel || '',
    };
  }

  // === Migrate ReadyNode-based specific fields ===
  if (hw.readyNodeSelection) {
    transformed.readyNodeBasedConfig = {
      readyNodeSelection: hw.readyNodeSelection,
      recommendedProfileName: hw.recommendedProfileName,
    };
  }

  // === Migrate Common fields ===
  transformed.commonConfig = {
    sockets: hw.sockets ?? 0,
    cores: hw.cores ?? 0,
    clockSpeed: hw.clockSpeed ?? 0,
    numberOfDrives: hw.numberOfDrives ?? 0,
    driveSize: hw.driveSize ?? 0,
    customDriveSize: hw.customDriveSize,
    serverRam: hw.serverRam ?? 0,
    networkSpeed: hw.networkSpeed ?? 0,
  };

  // === Keep optional vSAN fields ===
  // Check both root level and commonConfig for OSA-specific fields
  if (hw.cacheTierMediaRating !== undefined) {
    transformed.cacheTierMediaRating = hw.cacheTierMediaRating;
  } else if (hw.commonConfig?.cacheTierMediaRating !== undefined) {
    transformed.cacheTierMediaRating = hw.commonConfig.cacheTierMediaRating;
  }

  if (hw.cacheDriveSlots !== undefined) {
    transformed.cacheDriveSlots = hw.cacheDriveSlots;
  }

  if (hw.diskGroup !== undefined) {
    transformed.diskGroup = hw.diskGroup;
  } else if (hw.commonConfig?.diskGroup !== undefined) {
    transformed.diskGroup = hw.commonConfig.diskGroup;
  }

  if (hw.userDefinedDiskGroups !== undefined) {
    transformed.userDefinedDiskGroups = hw.userDefinedDiskGroups;
  } else if (hw.commonConfig?.userDefinedDiskGroups !== undefined) {
    transformed.userDefinedDiskGroups = hw.commonConfig.userDefinedDiskGroups;
  }

  return transformed as VCPM.HardwareConfiguration;
}
